import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/CheckApi';
import './CompanyList.css';

/* SMS GRUP | Firma Yönetimi
 * Mevcut firma CRUD/Excel API'leri korunur.
 * Yeni backend: GET /api/check/company-stats/
 * api.getCompanyStats varsa o kullanılır; yoksa same-origin API'ye istek atılır.
 */
const ICONS = {
  building: <><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M7 7h2m4 0h2m-8 4h2m4 0h2m-8 4h2m4 0h2M10 21v-4h4v4"/></>,
  cheques: <><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 10h18M7 15h4m4 0h2"/></>,
  activity: <><path d="M3 17h4l3-9 4 12 3-7h4"/></>,
  noChecks: <><rect x="4" y="4" width="16" height="16" rx="3"/><path d="m9 9 6 6m0-6-6 6"/></>,
  search: <><circle cx="11" cy="11" r="7"/><path d="m20 20-4-4"/></>,
  plus: <path d="M12 5v14M5 12h14"/>,
  download: <><path d="M12 3v12m-5-5 5 5 5-5M5 18v3h14v-3"/></>,
  refresh: <><path d="M20 11a8 8 0 0 0-14.5-4.6L3 9m0-6v6h6M4 13a8 8 0 0 0 14.5 4.6L21 15m0 6v-6h-6"/></>,
  filter: <><path d="M4 5h16L14 12v6l-4 2v-8z"/></>,
  pencil: <><path d="m4 20 4.5-1 11-11a2 2 0 0 0-3-3l-11 11L4 20ZM14 7l3 3"/></>,
  trash: <><path d="M4 7h16M9 7V4h6v3m3 0-1 13H7L6 7m4 4v6m4-6v6"/></>,
  eye: <><path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/></>,
  x: <path d="M5 5l14 14M19 5 5 19"/>,
  arrowLeft: <path d="m14 6-6 6 6 6"/>,
  arrowRight: <path d="m10 6 6 6-6 6"/>,
  info: <><circle cx="12" cy="12" r="9"/><path d="M12 11v6m0-10v.1"/></>,
  check: <path d="m5 12 4 4L19 6"/>,
  warning: <><path d="m12 3 10 18H2L12 3Z"/><path d="M12 9v4m0 3v.1"/></>,
  calendar: <><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M7 3v4m10-4v4M3 10h18"/></>,
  caret: <path d="m7 10 5 5 5-5"/>,
};

function Icon({ name, size = 20, ...rest }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...rest}>{ICONS[name]}</svg>;
}

const emptyForm = { title: '', tax_number: '', tax_office: '' };
const intFormatter = new Intl.NumberFormat('tr-TR');
const fmtInt = (value) => intFormatter.format(Number(value) || 0);
const fmtMoney = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (value) => {
  if (!value) return '—';
  const iso = String(value).split('T')[0];
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (match) return `${match[3]}.${match[2]}.${match[1]}`;
  return String(value);
};
const money = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? fmtMoney.format(n) : '—';
};
const currencySort = (a, b) => {
  const order = ['TL', 'USD', 'EUR', 'GBP'];
  const ai = order.indexOf(a.currency), bi = order.indexOf(b.currency);
  return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi) || a.currency.localeCompare(b.currency, 'tr');
};
const normalize = (s) => String(s ?? '').toLocaleLowerCase('tr-TR').trim();
const getError = (e) => {
  const detail = e?.response?.data?.detail || e?.message || 'Bilinmeyen hata';
  return typeof detail === 'string' ? detail : 'İşlem tamamlanamadı.';
};

// API bazen Axios response, bazen doğrudan JSON döndürebilir.
// Eksik 'summary' / 'totals' bilgisi React render hatasına dönüşmesin.
function normalizeCompanyStats(response) {
  const payload = response?.data?.companies ? response.data : response;
  if (!payload || !Array.isArray(payload.companies)) {
    throw new Error('İstatistik API yanıtında companies listesi bulunamadı.');
  }
  const rawSummary = payload.summary;
  if (!rawSummary || !Array.isArray(rawSummary.totals)) {
    throw new Error('İstatistik API yanıtında summary.totals alanı eksik.');
  }
  const toNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };
  const cleanTotals = (items) => (Array.isArray(items) ? items : []).filter(
    (item) => item && item.currency != null
  ).map((item) => ({
    currency: String(item.currency),
    amount: item.amount ?? '0',
    check_count: toNumber(item.check_count),
  }));
  return {
    summary: {
      total_companies: toNumber(rawSummary.total_companies),
      companies_with_checks: toNumber(rawSummary.companies_with_checks),
      companies_without_checks: toNumber(rawSummary.companies_without_checks),
      matched_checks: toNumber(rawSummary.matched_checks),
      unmatched_checks: toNumber(rawSummary.unmatched_checks),
      invalid_amount_checks: toNumber(rawSummary.invalid_amount_checks),
      totals: cleanTotals(rawSummary.totals),
    },
    companies: payload.companies.filter(Boolean).map((item) => ({
      ...item,
      check_count: toNumber(item.check_count),
      totals: cleanTotals(item.totals),
    })),
  };
}

async function requestCompanyStats() {
  if (typeof api.getCompanyStats === 'function') {
    const result = await api.getCompanyStats();
    return normalizeCompanyStats(result);
  }
  // Üretimde frontend ile backend aynı https origin'deyse doğrudan çalışır.
  // Ayrı origin kullanılıyorsa CheckApi'ye getCompanyStats ekleyin (README).
  const token = localStorage.getItem('token');
  const res = await fetch('/api/check/company-stats/', {
    method: 'GET',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), Accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail || `Firma çek istatistikleri alınamadı (HTTP ${res.status}).`);
  }
  return normalizeCompanyStats(await res.json());
}

function StatCard({ icon, label, value, note, tone }) {
  return <div className="cl-stat-card">
    <span className={`cl-stat-icon cl-stat-icon--${tone}`}><Icon name={icon} size={21} /></span>
    <div className="cl-stat-value">{value}</div>
    <div className="cl-stat-label">{label}</div>
    <div className="cl-stat-note">{note}</div>
  </div>;
}

function CurrencyPills({ totals = [], muted = false }) {
  if (!totals?.length) return <span className="cl-muted-amount">Çek kaydı yok</span>;
  return <div className="cl-currencies">
    {[...totals].sort(currencySort).map((item) => <span className={`cl-currency-pill ${muted ? 'cl-currency-pill--quiet' : ''}`} key={item.currency}>
      <strong>{money(item.amount)}</strong><span>{item.currency}</span>
    </span>)}
  </div>;
}

function ModalShell({ title, subtitle, children, onClose, className = '' }) {
  return <div className="cl-modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
    <section className={`cl-modal ${className}`} role="dialog" aria-modal="true" aria-label={title}>
      <div className="cl-modal-heading"><div><h2>{title}</h2>{subtitle && <p>{subtitle}</p>}</div>
        <button className="cl-icon-btn" type="button" onClick={onClose} aria-label="Kapat"><Icon name="x" /></button>
      </div>
      {children}
    </section>
  </div>;
}

// Firma bileşeninde beklenmeyen bir React render hatası oluşursa
// tüm CheckLayout/AppShell'in boşalmasını önleyen yerel güvenlik katmanı.
class CompanyListErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error('[Firma Yönetimi] React render hatası:', error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <section className="cl-page" role="alert">
          <div className="cl-render-error">
            <h2>Firma Yönetimi görüntülenemedi</h2>
            <p>Sayfa oluşturulurken beklenmeyen bir hata meydana geldi. Diğer portal sayfalarını kullanmaya devam edebilirsiniz.</p>
            <p>Hata ayrıntısını tarayıcının <strong>F12 → Console</strong> bölümünde görebilirsiniz.</p>
            <button className="cl-btn cl-btn--primary" type="button" onClick={() => window.location.reload()}>Sayfayı Yenile</button>
          </div>
        </section>
      );
    }
    return this.props.children;
  }
}

export default function CompanyList() {
  return <CompanyListErrorBoundary><CompanyListContent /></CompanyListErrorBoundary>;
}

function CompanyListContent() {
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statsError, setStatsError] = useState('');
  const [listError, setListError] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('name');
  const [page, setPage] = useState(1);
  const [formMode, setFormMode] = useState(null); // null | add | edit
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [viewCompany, setViewCompany] = useState(null);
  const [deleteCompany, setDeleteCompany] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [notice, setNotice] = useState(null);
  const noticeTimer = useRef(null);
  const PAGE_SIZE = 10;

  const notify = useCallback((kind, text) => {
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    setNotice({ kind, text });
    noticeTimer.current = setTimeout(() => setNotice(null), 5000);
  }, []);
  useEffect(() => () => { if (noticeTimer.current) clearTimeout(noticeTimer.current); }, []);

  const refreshData = useCallback(async ({ initial = false } = {}) => {
    if (initial) setLoading(true);
    else setRefreshing(true);
    // Bir isteğin hatası diğer tablonun çalışmasını engellememeli.
    const [companyResult, statsResult] = await Promise.allSettled([
      Promise.resolve().then(() => api.getCompanies()),
      Promise.resolve().then(() => requestCompanyStats()),
    ]);
    if (companyResult.status === 'fulfilled') {
      const value = companyResult.value;
      const list = Array.isArray(value) ? value : Array.isArray(value?.data) ? value.data : null;
      if (list) { setCompanies(list.filter((item) => item && typeof item === 'object')); setListError(''); }
      else setListError('Firma API yanıtı beklenen liste biçiminde değil.');
    } else setListError(`Firmalar yüklenemedi: ${getError(companyResult.reason)}`);
    if (statsResult.status === 'fulfilled' && Array.isArray(statsResult.value?.companies) && statsResult.value.summary) {
      setStats(statsResult.value); setStatsError('');
    } else {
      setStats(null);
      setStatsError(statsResult.status === 'rejected'
        ? getError(statsResult.reason)
        : 'İstatistik servisi beklenen veri biçiminde yanıt vermedi.');
    }
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => { refreshData({ initial: true }); }, [refreshData]);
  useEffect(() => { setPage(1); }, [search, filter, sort]);
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') { setFormMode(null); setViewCompany(null); setDeleteCompany(null); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const statsMap = useMemo(() => new Map((stats?.companies || []).map((c) => [String(c.id), c])), [stats]);
  const rows = useMemo(() => companies.map((c) => ({ ...c, analytics: statsMap.get(String(c.id)) || null })), [companies, statsMap]);
  const filtered = useMemo(() => {
    const term = normalize(search);
    const subset = rows.filter((item) => {
      const matches = !term || [item.title, item.tax_number, item.tax_office].some((v) => normalize(v).includes(term));
      if (!matches) return false;
      if (filter === 'with') return (item.analytics?.check_count ?? 0) > 0;
      if (filter === 'without') return (item.analytics?.check_count ?? 0) === 0;
      return true;
    });
    subset.sort((a, b) => {
      if (sort === 'most') return (b.analytics?.check_count ?? 0) - (a.analytics?.check_count ?? 0) || String(a.title).localeCompare(String(b.title), 'tr');
      if (sort === 'least') return (a.analytics?.check_count ?? 0) - (b.analytics?.check_count ?? 0) || String(a.title).localeCompare(String(b.title), 'tr');
      if (sort === 'recent') return String(b.analytics?.last_check_date || '').localeCompare(String(a.analytics?.last_check_date || '')) || String(a.title).localeCompare(String(b.title), 'tr');
      if (sort === 'name-desc') return String(b.title).localeCompare(String(a.title), 'tr');
      return String(a.title).localeCompare(String(b.title), 'tr');
    });
    return subset;
  }, [rows, search, filter, sort]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const visibleRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const summary = stats?.summary ?? null;
  const hasStats = Boolean(summary && Array.isArray(summary.totals));

  const openAdd = () => { setForm({ ...emptyForm }); setEditId(null); setFormMode('add'); };
  const openEdit = (company) => {
    setForm({ title: company.title || '', tax_number: String(company.tax_number ?? ''), tax_office: company.tax_office || '' });
    setEditId(company.id);
    setFormMode('edit');
  };

  const saveCompany = async (event) => {
    event.preventDefault();
    const payload = {
      title: form.title.trim(),
      tax_number: form.tax_number.trim(),
      tax_office: form.tax_office.trim(),
    };
    if (!payload.title || !payload.tax_number) {
      notify('error', 'Firma ünvanı ve vergi numarası zorunludur.');
      return;
    }
    setBusy(true);
    try {
      if (formMode === 'edit') await api.updateCompany(editId, payload);
      else await api.createCompany(payload);
      setFormMode(null);
      notify('success', formMode === 'edit' ? 'Firma bilgileri güncellendi.' : 'Firma başarıyla kaydedildi.');
      await refreshData();
    } catch (e) { notify('error', `Firma kaydedilemedi: ${getError(e)}`); }
    finally { setBusy(false); }
  };

  const confirmDelete = async () => {
    if (!deleteCompany) return;
    setBusy(true);
    try {
      await api.deleteCompany(deleteCompany.id);
      setDeleteCompany(null);
      notify('success', 'Firma silindi.');
      await refreshData();
    } catch (e) { notify('error', `Firma silinemedi: ${getError(e)}`); }
    finally { setBusy(false); }
  };

  const downloadExcel = async () => {
    setExporting(true);
    try {
      // Eski Excel endpointini değiştirmiyoruz.
      const result = await api.exportCompaniesExcel();
      const blob = result instanceof Blob ? result : result?.data;
      if (!(blob instanceof Blob)) throw new Error('Excel indirme API yanıtı Blob değil.');
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `firma_rehberi_${new Date().toLocaleDateString('sv-SE')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      notify('success', 'Firma listesi Excel olarak indirildi.');
    } catch (e) { notify('error', `Excel indirilemedi: ${getError(e)}`); }
    finally { setExporting(false); }
  };

  return <main className="cl-page">
    <div className="cl-container">
      <div className="cl-hero">
        <div className="cl-hero-content">
          <div className="cl-eyebrow"><span className="cl-eyebrow-dot"/> SMS GRUP PORTAL <span className="cl-eyebrow-sep">/</span> ÇEK YÖNETİMİ</div>
          <h1>Firma Yönetimi <span>& Portföy Analizi</span></h1>
          <p>Firma kayıtlarını yönetin, firmalara yazılan çeklerin adetlerini ve para birimi bazındaki tutarlarını tek ekrandan izleyin.</p>
          <div className="cl-hero-meta"><Icon name="check" size={15}/> Güncel firma rehberi <span className="cl-hero-divider"/> <Icon name="activity" size={15}/> Çek kayıtlarından canlı hesaplama</div>
        </div>
        <div className="cl-hero-actions">
          <button type="button" className="cl-btn cl-btn--hero-ghost" disabled={refreshing || loading} onClick={() => refreshData()}><Icon name="refresh" size={17}/> {refreshing ? 'Yenileniyor...' : 'Yenile'}</button>
          <button type="button" className="cl-btn cl-btn--hero-ghost" disabled={exporting} onClick={downloadExcel}><Icon name="download" size={17}/> {exporting ? 'İndiriliyor...' : 'Excel İndir'}</button>
          <button type="button" className="cl-btn cl-btn--white" onClick={openAdd}><Icon name="plus" size={18}/> Yeni Firma</button>
        </div>
      </div>

      {notice && <div className={`cl-alert cl-alert--${notice.kind}`} role="status"><Icon name={notice.kind === 'success' ? 'check' : 'warning'} size={18}/>{notice.text}<button type="button" onClick={() => setNotice(null)} aria-label="Bildirimi kapat"><Icon name="x" size={16}/></button></div>}
      {listError && <div className="cl-alert cl-alert--error" role="alert"><Icon name="warning" size={18}/>{listError}</div>}
      {statsError && <div className="cl-alert cl-alert--warning" role="alert"><Icon name="warning" size={19}/><div><strong>Çek istatistikleri alınamadı.</strong><span> {statsError} — Firma listesi kullanılabilir; çek adetleri ve tutarlar doğrulanana kadar gösterilmiyor.</span></div></div>}

      <section className="cl-stats-grid" aria-label="Firma özeti">
        <StatCard icon="building" label="Kayıtlı Firma" value={loading ? '...' : fmtInt(rows.length)} note="Rehberdeki tüm firmalar" tone="blue"/>
        <StatCard icon="cheques" label="Eşleşen Çek Sayısı" value={hasStats ? fmtInt(summary.matched_checks) : '—'} note="Firma kayıtlarıyla eşleşen çekler" tone="indigo"/>
        <StatCard icon="activity" label="Çek Yazılan Firma" value={hasStats ? fmtInt(summary.companies_with_checks) : '—'} note="En az bir çek kaydı bulunan" tone="green"/>
        <StatCard icon="noChecks" label="Çek Kaydı Olmayan" value={hasStats ? fmtInt(summary.companies_without_checks) : '—'} note="Henüz çek eşleşmesi yok" tone="amber"/>
      </section>

      <section className="cl-currency-overview" aria-label="Para birimlerine göre çek tutarları">
        <div className="cl-overview-top">
          <div className="cl-overview-title"><span className="cl-overview-icon"><Icon name="activity" size={20}/></span><div><h2>Portföy Özeti</h2><p>Firmalarla eşleşen çeklerin toplam tutarları</p></div></div>
          <div className="cl-overview-hint"><Icon name="info" size={15}/> Para birimleri birbirine çevrilmez</div>
        </div>
        {!hasStats ? <div className="cl-overview-empty">{loading ? 'Hesaplanıyor...' : 'Tutar bilgileri şu anda görüntülenemiyor.'}</div>
          : summary.totals.length === 0 ? <div className="cl-overview-empty">Henüz firmalarla eşleşen çek kaydı bulunmuyor.</div>
          : <div className="cl-overview-currencies">{[...summary.totals].sort(currencySort).map(item => <div key={item.currency} className="cl-overview-currency">
            <div className="cl-overview-currency-top"><span>{item.currency}</span><span>{fmtInt(item.check_count)} çek</span></div>
            <strong>{money(item.amount)}</strong>
            <small>{item.currency} cinsinden yazılan çekler</small>
          </div>)}</div>}
        {hasStats && (summary.unmatched_checks > 0 || summary.invalid_amount_checks > 0) && <div className="cl-data-quality"><Icon name="info" size={16}/> {summary.unmatched_checks > 0 && `${fmtInt(summary.unmatched_checks)} çek kaydı firmayla eşleştirilemedi. `}{summary.invalid_amount_checks > 0 && `${fmtInt(summary.invalid_amount_checks)} kaydın tutarı okunamadığı için toplam tutara dahil edilmedi.`}</div>}
      </section>

      <section className="cl-table-panel">
        <div className="cl-panel-heading"><div><h2>Firma Listesi</h2><p>Her firmaya yazılan çekleri ve toplam tutarları inceleyin.</p></div><span className="cl-count-badge">{fmtInt(filtered.length)} firma</span></div>
        <div className="cl-toolbar">
          <label className="cl-search"><Icon name="search" size={19}/><input aria-label="Firma ara" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Firma adı, vergi no veya vergi dairesi ara..." /></label>
          <div className="cl-filter-segment" role="group" aria-label="Çek kaydı filtresi">
            <button type="button" className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>Tümü</button>
            <button type="button" className={filter === 'with' ? 'active' : ''} onClick={() => setFilter('with')} disabled={!hasStats}>Çekli Firmalar</button>
            <button type="button" className={filter === 'without' ? 'active' : ''} onClick={() => setFilter('without')} disabled={!hasStats}>Çeksiz Firmalar</button>
          </div>
          <label className="cl-sort"><Icon name="filter" size={16}/><select aria-label="Sıralama" value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="name">İsme Göre (A–Z)</option><option value="name-desc">İsme Göre (Z–A)</option><option value="most" disabled={!hasStats}>En Çok Çek</option><option value="least" disabled={!hasStats}>En Az Çek</option><option value="recent" disabled={!hasStats}>En Son Çek Tarihi</option>
          </select><Icon name="caret" size={15}/></label>
        </div>
        <div className="cl-table-scroll"><table className="cl-table">
          <thead><tr><th>FİRMA BİLGİLERİ</th><th>VERGİ BİLGİLERİ</th><th>ÇEK ADEDİ</th><th>YAZILAN ÇEK TUTARI</th><th>SON ÇEK TARİHİ</th><th className="cl-action-col">İŞLEMLER</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6}><div className="cl-placeholder"><div className="cl-spinner"/> Firmalar yükleniyor...</div></td></tr>
              : visibleRows.length === 0 ? <tr><td colSpan={6}><div className="cl-placeholder"><Icon name="building" size={29}/><strong>{search || filter !== 'all' ? 'Eşleşen firma bulunamadı' : 'Henüz kayıtlı firma bulunmuyor'}</strong><span>{search || filter !== 'all' ? 'Arama veya filtreyi değiştirerek tekrar deneyin.' : 'Yeni Firma butonuyla ilk kaydı oluşturabilirsiniz.'}</span></div></td></tr>
              : visibleRows.map((item) => <tr key={item.id}>
                <td><div className="cl-company-cell"><span className="cl-company-avatar">{String(item.title || '?').trim().charAt(0).toLocaleUpperCase('tr-TR')}</span><div className="cl-company-name"><strong title={item.title}>{item.title}</strong><span>Firma No: {item.id}</span></div></div></td>
                <td><div className="cl-tax"><span>{item.tax_number || '—'}</span><small>{item.tax_office || 'Vergi dairesi belirtilmedi'}</small></div></td>
                <td>{hasStats ? <div className="cl-count-cell"><strong>{fmtInt(item.analytics?.check_count || 0)}</strong><span className={(item.analytics?.check_count || 0) ? 'cl-status cl-status--active' : 'cl-status cl-status--none'}>{(item.analytics?.check_count || 0) ? 'Çek kaydı var' : 'Kayıt yok'}</span></div> : <span className="cl-data-unknown">—</span>}</td>
                <td className="cl-amount-col">{hasStats ? <CurrencyPills totals={item.analytics?.totals || []}/> : <span className="cl-data-unknown">Veri alınamadı</span>}</td>
                <td><span className="cl-last-date"><Icon name="calendar" size={15}/>{hasStats ? fmtDate(item.analytics?.last_check_date) : '—'}</span></td>
                <td><div className="cl-row-actions">
                  <button type="button" className="cl-row-btn" title="Firma özetini görüntüle" aria-label={`${item.title} detay`} onClick={() => setViewCompany(item)}><Icon name="eye" size={17}/></button>
                  <button type="button" className="cl-row-btn" title="Düzenle" aria-label={`${item.title} düzenle`} onClick={() => openEdit(item)}><Icon name="pencil" size={17}/></button>
                  <button type="button" className="cl-row-btn cl-row-btn--danger" title="Sil" aria-label={`${item.title} sil`} onClick={() => setDeleteCompany(item)}><Icon name="trash" size={17}/></button>
                </div></td>
              </tr>)}
          </tbody>
        </table></div>
        <div className="cl-table-footer"><span>{filtered.length === 0 ? '0 firma' : `${(safePage - 1) * PAGE_SIZE + 1}–${Math.min(safePage * PAGE_SIZE, filtered.length)} / ${fmtInt(filtered.length)} firma`}</span>
          <div className="cl-pagination"><button type="button" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)} aria-label="Önceki sayfa"><Icon name="arrowLeft" size={16}/></button><span>Sayfa <strong>{safePage}</strong> / {pageCount}</span><button type="button" disabled={safePage >= pageCount} onClick={() => setPage(safePage + 1)} aria-label="Sonraki sayfa"><Icon name="arrowRight" size={16}/></button></div>
        </div>
      </section>
      <p className="cl-footnote"><Icon name="info" size={15}/> Buradaki bilgiler çek kayıtlarının firma ve vergi numarasıyla eşleştirilmesiyle hesaplanır. Çek tarihi, tahsilat veya ödeme durumunu göstermez.</p>
    </div>

    {formMode && <ModalShell title={formMode === 'edit' ? 'Firmayı Düzenle' : 'Yeni Firma Ekle'} subtitle="Firma bilgilerini eksiksiz girin." onClose={() => { if (!busy) setFormMode(null); }}>
      <form className="cl-form" onSubmit={saveCompany}>
        <label>Firma Ünvanı <span>*</span><input name="title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Örn: ABC İnşaat San. ve Tic. A.Ş." required maxLength={250} autoFocus /></label>
        <div className="cl-form-row"><label>Vergi Numarası <span>*</span><input name="tax_number" value={form.tax_number} onChange={(e) => setForm({ ...form, tax_number: e.target.value })} placeholder="Vergi numarası" required maxLength={30}/></label><label>Vergi Dairesi<input name="tax_office" value={form.tax_office} onChange={(e) => setForm({ ...form, tax_office: e.target.value })} placeholder="Örn: Çankaya" maxLength={120}/></label></div>
        {formMode === 'edit' && <p className="cl-form-hint"><Icon name="warning" size={16}/> Vergi numarasını değiştirmek, geçmiş çek kayıtlarıyla eşleşmeyi etkileyebilir.</p>}
        <div className="cl-modal-actions"><button type="button" className="cl-btn cl-btn--subtle" onClick={() => setFormMode(null)} disabled={busy}>Vazgeç</button><button type="submit" className="cl-btn cl-btn--primary" disabled={busy}><Icon name="check" size={17}/>{busy ? 'Kaydediliyor...' : 'Kaydet'}</button></div>
      </form>
    </ModalShell>}

    {viewCompany && <ModalShell title="Firma Portföyü" subtitle="Firma bazında kayıtlı çek özeti" onClose={() => setViewCompany(null)} className="cl-detail-modal">
      <div className="cl-detail-profile"><div className="cl-detail-avatar">{String(viewCompany.title || '?').trim().charAt(0).toLocaleUpperCase('tr-TR')}</div><div><h3>{viewCompany.title}</h3><p>VKN: {viewCompany.tax_number || '—'} <span>•</span> {viewCompany.tax_office || 'Vergi dairesi belirtilmemiş'}</p></div></div>
      {hasStats ? <><div className="cl-detail-kpis"><div><span>Toplam Çek</span><strong>{fmtInt(viewCompany.analytics?.check_count || 0)}</strong></div><div><span>İlk Çek Tarihi</span><strong>{fmtDate(viewCompany.analytics?.first_check_date)}</strong></div><div><span>Son Çek Tarihi</span><strong>{fmtDate(viewCompany.analytics?.last_check_date)}</strong></div></div>
        <div className="cl-detail-title">Para Birimi Bazında Tutarlar</div>
        {viewCompany.analytics?.totals?.length ? <div className="cl-detail-amounts">{[...viewCompany.analytics.totals].sort(currencySort).map((t) => <div key={t.currency}><div><span className="cl-detail-currency">{t.currency}</span><span>{fmtInt(t.check_count)} çek</span></div><strong>{money(t.amount)} {t.currency}</strong></div>)}</div>
          : <div className="cl-overview-empty">Bu firmaya ait eşleşen çek kaydı bulunmuyor.</div>}</>
        : <div className="cl-overview-empty">Çek istatistikleri şu anda kullanılamıyor.</div>}
      <div className="cl-modal-actions"><button className="cl-btn cl-btn--subtle" type="button" onClick={() => setViewCompany(null)}>Kapat</button><button className="cl-btn cl-btn--primary" type="button" onClick={() => { setViewCompany(null); openEdit(viewCompany); }}><Icon name="pencil" size={16}/> Firmayı Düzenle</button></div>
    </ModalShell>}

    {deleteCompany && <ModalShell title="Firma Silme Onayı" subtitle="Bu işlem geri alınamaz." onClose={() => { if (!busy) setDeleteCompany(null); }} className="cl-confirm-modal">
      <div className="cl-delete-icon"><Icon name="warning" size={27}/></div>
      <p className="cl-confirm-text"><strong>{deleteCompany.title}</strong> firmasını silmek istediğinize emin misiniz?</p>
      {(deleteCompany.analytics?.check_count || 0) > 0 && <p className="cl-confirm-warning">Bu firmaya bağlı {fmtInt(deleteCompany.analytics.check_count)} çek kaydı bulunuyor. Silme sonrasında geçmiş çekler firma listesiyle eşleşmeyebilir.</p>}
      <div className="cl-modal-actions"><button type="button" className="cl-btn cl-btn--subtle" disabled={busy} onClick={() => setDeleteCompany(null)}>Vazgeç</button><button type="button" className="cl-btn cl-btn--danger" disabled={busy} onClick={confirmDelete}><Icon name="trash" size={16}/>{busy ? 'Siliniyor...' : 'Evet, Sil'}</button></div>
    </ModalShell>}
  </main>;
}
